import React, { useState } from "react";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";

interface Props {
  label: string;
  name: string;
  id?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  defaultCountry?: string;
}

type CountryCode = string;

export const PhoneInput: React.FC<Props> = ({
  label,
  name,
  id,
  placeholder,
  value,
  onChange,
  error,
  defaultCountry = "SE", // Sweden as default
}) => {
  const inputId = id || name;

  // Parse the value to get country and number
  const getCountryAndNumber = (fullValue: string) => {
    if (!fullValue || fullValue.trim() === "") {
      return { country: defaultCountry, number: "" };
    }
    const phoneNumber = parsePhoneNumberFromString(fullValue);
    if (phoneNumber) {
      return {
        country: phoneNumber.country || defaultCountry,
        number: phoneNumber.nationalNumber,
      };
    }
    return { country: defaultCountry, number: fullValue.replace(/^\+\d+/, "") };
  };

  const { country: initialCountry, number: initialNumber } = getCountryAndNumber(value);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(initialCountry);
  const [phoneNumber, setPhoneNumber] = useState<string>(initialNumber);

  const getCountryName = (countryCode: string) => {
    try {
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
      return regionNames.of(countryCode) || countryCode;
    } catch (e) {
      return countryCode;
    }
  };

  const countries = getCountries().sort((a, b) => {
    const nameA = getCountryName(a);
    const nameB = getCountryName(b);
    return nameA.localeCompare(nameB);
  });

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountry = e.target.value as CountryCode;
    setSelectedCountry(newCountry);
    const callingCode = getCountryCallingCode(newCountry as any);
    const fullNumber = phoneNumber ? `+${callingCode}${phoneNumber}` : "";
    onChange(fullNumber);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/\D/g, ""); // Remove non-digits
    
    // Allow user to see the leading "0" in the input
    setPhoneNumber(newNumber);
    
    // But remove leading "0" before sending to backend (for Swedish numbers)
    let normalizedNumber = newNumber;
    if (selectedCountry === "SE" && newNumber.startsWith("0")) {
      normalizedNumber = newNumber.substring(1);
    }
    
    const callingCode = getCountryCallingCode(selectedCountry as any);
    const fullNumber = normalizedNumber ? `+${callingCode}${normalizedNumber}` : "";
    onChange(fullNumber);
  };

  const callingCode = getCountryCallingCode(selectedCountry as any);
  const getFlagEmoji = (countryCode: string) => {
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <div className="flex gap-2">
        {/* Country Selector with Flag and Code */}
        <div className="relative">
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            className={`h-full w-25 px-3 py-2 border rounded-md bg-gray-50 hover:bg-gray-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-8 country-select ${error ? "border-red-500" : "border-gray-300"
              }`}          >
            {countries.map((country) => (
              <option key={country} value={country}>
                {getCountryName(country)}
              </option>
            ))}
          </select>
          {/* Custom display for the selected value */}
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-3">
            <span className="text-base">
              {getFlagEmoji(selectedCountry)} +{callingCode}
            </span>
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg
              className="fill-current h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>

        {/* Phone Number Input */}
        <input
          id={inputId}
          name={name}
          type="tel"
          placeholder={placeholder}
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          className={`flex-1 w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? "border-red-500" : "border-gray-300"
            }`}
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
